import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Banner,
    Button,
    FormField,
    FormHelp,
    Input,
    Label,
    Spacer,
    Text,
} from '@zillow/constellation';

import { GA_EVENTS_INTERESTED_RENTERS_MODULE } from 'app/shared/constants/AnalyticsConstants';
import { PropertiesSelectors, RoutingSelectors } from 'app/shared/selectors';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import appLogger from 'app/shared/loggers/appLogger';
import ListingActivationTerm from 'app/shared/components/ListingActivationTerms';
import ApplicationActions from 'app/shared/actions/ApplicationActions';
import KeyCodeConstants from 'app/shared/constants/KeyCodeConstants';
import AdditionalInformation from './AdditionalInformation';

const {
    INVITE_TO_APPLY_SEND_INVITATION_SUBMIT,
} = GA_EVENTS_INTERESTED_RENTERS_MODULE;

const HEADING_TEXT = 'Invite a potential tenant to apply';
const INVALID_EMAIL_MSG = 'please enter a valid email address';
const INVALID_NAME_MSG = 'please enter a valid name not exceeding 32 letters';

const InviteToApplyModal = () => {
    const dispatch = useDispatch();
    const currentProperty = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));
    const listingId = useSelector(state => RoutingSelectors.makeListingId(state));

    // Form Fields
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [showSuccessBanner, setShowSuccessBanner] = useState(false);
    const [showFailureBanner, setShowFailureBanner] = useState(false);
    const [successBannerText, setSuccessBannerText] = useState('');

    // Form Field Errors
    const [errorObj, setErrorObj] = useState({
        firstName: '',
        lastName: '',
        email: '',
    });

    const { street } = currentProperty;

    const isErrorFree = (() => {
        for (const key in errorObj) {
            if (errorObj[key] !== null && errorObj[key] !== '') {
                return false;
            }
        }
        return true;
    })();

    const isButtonEnabled = !!firstName && !!lastName && !!email && isErrorFree;
    const headingDisplay = street ? `${HEADING_TEXT} to ${street}` : HEADING_TEXT;

    const handleOnChange = (e, callback) => {
        // Clear banners
        if (showFailureBanner) {
            setShowFailureBanner(false);
        }

        if (showSuccessBanner) {
            setShowSuccessBanner(false);
        }

        const { value } = e.target;
        callback(value);
    };

    const handleSubmission = () => {
        // Clear Success Banner
        setShowSuccessBanner(false);
        setShowFailureBanner(false);

        const requestBody = {
            listingId,
            renterFirstName: firstName,
            renterLastName: lastName,
            toEmail: email,
        };

        dispatch(ApplicationActions.inviteToApply(requestBody))
            .then((res) => {
                if (res.success) {
                    setShowSuccessBanner(true);
                    setSuccessBannerText(`You have invited ${firstName} ${lastName} to apply to your rental.`);
                    setFirstName('');
                    setLastName('');
                    setEmail('');

                    dispatch(analyticsUtils.track.GAEventV2(INVITE_TO_APPLY_SEND_INVITATION_SUBMIT));
                } else {
                    setShowFailureBanner(true);
                    appLogger.logError({
                        message: `Could not invite user to apply. Response: ${JSON.stringify(res)}.`,
                        context: 'InviteToApplyModal.handleSubmission',
                        listingId,
                    });
                }
            })
            .catch((err) => {
                appLogger.logError({
                    message: `Could not invite user to apply. Error: ${JSON.stringify(err)}.`,
                    context: 'InviteToApplyModal.handleSubmission',
                    listingId,
                });

                setShowFailureBanner(true);
            });
    };

    const handleKeyDown = (e) => {
        const { keyCode } = e;

        // if enter is pressed
        if (keyCode === KeyCodeConstants.ENTER) {
            handleSubmission();
        }
    };

    const validateFirstOrLastName = (name) => {
        const isValidLength = (name && name.length > 0 && name.length < 33);
        return isValidLength ? null : INVALID_NAME_MSG;
    };

    const handleFirstNameChange = () => {
        const errorMessage = validateFirstOrLastName(firstName);

        setErrorObj({
            ...errorObj,
            firstName: errorMessage,
        });
    };

    const handleLastNameChange = () => {
        const errorMessage = validateFirstOrLastName(lastName);

        setErrorObj({
            ...errorObj,
            lastName: errorMessage,
        });
    };

    const handleEmailChange = () => {
        const isValidEmail = /(.+?)@(.+?)\.[a-zA-Z]{1,}/.test(email);
        const errorMessage = isValidEmail ? null : INVALID_EMAIL_MSG;

        setErrorObj({
            ...errorObj,
            email: errorMessage,
        });
    };

    return (
        <>
            {
                showSuccessBanner && (
                    <Banner
                        appearance="success"
                        body={successBannerText}
                    />
                )
            }

            {
                showFailureBanner && (
                    <Banner
                        appearance="error"
                        body={`There was an error inviting ${firstName} ${lastName} to apply to your rental.`}
                    />
                )
            }

            <Text
                paddingY="md"
                fontType="h6"
                as="div"
            >
                {headingDisplay}
            </Text>

            <FormField paddingY={1.5}>
                <Label>Tenant first name</Label>

                <Input
                    aria-label="Tenant first name"
                    onBlur={handleFirstNameChange}
                    onChange={(e) => { handleOnChange(e, setFirstName); }}
                    value={firstName}
                />

                <FormHelp error={!!errorObj.firstName}>
                    {errorObj.firstName}
                </FormHelp>
            </FormField>

            <FormField paddingY={1.5}>
                <Label>Tenant last name</Label>

                <Input
                    aria-label="Tenant last name"
                    onBlur={handleLastNameChange}
                    onChange={(e) => { handleOnChange(e, setLastName); }}
                    value={lastName}
                />

                <FormHelp error={!!errorObj.lastName}>
                    {errorObj.lastName}
                </FormHelp>
            </FormField>

            <FormField paddingY={1.5}>
                <Label>Tenant email</Label>

                <Input
                    aria-label="Tenant first name"
                    onBlur={handleEmailChange}
                    onChange={(e) => { handleOnChange(e, setEmail); }}
                    value={email}
                    onKeyDown={handleKeyDown}
                />

                <FormHelp error={!!errorObj.email}>
                    {errorObj.email}
                </FormHelp>
            </FormField>

            <Button
                marginY="xs"
                fluid={true}
                buttonType="primary"
                onClick={handleSubmission}
                disabled={!isButtonEnabled}
            >
                Send invitation
            </Button>

            <ListingActivationTerm
                lowlight={true}
                actionText="By clicking Send invitation"
            />
            <Spacer marginBottom="sm">
                <AdditionalInformation />
            </Spacer>
        </>
    );
};

export default InviteToApplyModal;
